import { useQuery } from "@tanstack/react-query";
import { fetcher } from "@/shared/api";
import { supportTicketKeys } from "./queryKeys";
import type {
  SupportTicketDetail,
  SupportTicketDetailResponse,
  SupportTicketListFilter,
  SupportTicketListItem,
  SupportTicketListResponse,
  SupportTicketAssignee,
  SupportTicketAssigneesResponse,
  SupportTicketStats,
  SupportTicketStatsResponse,
  SystemSupportTicketDetail,
  SystemSupportTicketDetailResponse,
  SystemSupportTicketListFilter,
  SystemSupportTicketListItem,
  SystemSupportTicketListResponse,
} from "../model/types";

function withNormalizedId<T extends { id?: string }>(
  raw: T & { _id?: string },
): T & { id: string } {
  return { ...raw, id: raw.id ?? raw._id ?? "" };
}

function docsOf(res: SupportTicketListResponse): SupportTicketListItem[] {
  const docs = Array.isArray(res.data) ? res.data : res.data.docs;
  return docs.map(withNormalizedId);
}

function totalOf(res: SupportTicketListResponse): number {
  return Array.isArray(res.data) ? res.data.length : res.data.pagination.total;
}

export interface SupportTicketListPage {
  items: SupportTicketListItem[];
  total: number;
}

async function fetchByFilter(
  filter: SupportTicketListFilter,
  search: string,
  page: number,
  limit: number,
): Promise<SupportTicketListPage> {
  const trimmedSearch = search.trim();

  const buildQuery = (extra: Record<string, string>) => {
    const params = new URLSearchParams(extra);
    if (trimmedSearch) params.set("search", trimmedSearch);
    return params.toString();
  };

  if (filter === "ALL") {
    const res = await fetcher<SupportTicketListResponse>(
      `/support-tickets?${buildQuery({ page: String(page), limit: String(limit) })}`,
    );
    return { items: docsOf(res), total: totalOf(res) };
  }

  if (filter === "OPEN") {
    const res = await fetcher<SupportTicketListResponse>(
      `/support-tickets?${buildQuery({ page: String(page), limit: String(limit), openOnly: "true" })}`,
    );
    return { items: docsOf(res), total: totalOf(res) };
  }

  const [resolved, closed] = await Promise.all([
    fetcher<SupportTicketListResponse>(
      `/support-tickets?${buildQuery({ paginate: "false", status: "RESOLVED" })}`,
    ),
    fetcher<SupportTicketListResponse>(
      `/support-tickets?${buildQuery({ paginate: "false", status: "CLOSED" })}`,
    ),
  ]);
  const merged = [...docsOf(resolved), ...docsOf(closed)].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
  const start = (page - 1) * limit;
  return { items: merged.slice(start, start + limit), total: merged.length };
}

export function useSupportTicketList(
  filter: SupportTicketListFilter = "ALL",
  search = "",
  page = 1,
  limit = 10,
) {
  return useQuery<SupportTicketListPage>({
    queryKey: supportTicketKeys.list(filter, search.trim(), page, limit),
    queryFn: () => fetchByFilter(filter, search, page, limit),
  });
}

export function useSupportTicketDetail(ticketId: string) {
  return useQuery<
    SupportTicketDetailResponse,
    unknown,
    SupportTicketDetail
  >({
    queryKey: supportTicketKeys.detail(ticketId),
    queryFn: () =>
      fetcher<SupportTicketDetailResponse>(`/support-tickets/${ticketId}`),
    select: (res) => withNormalizedId(res.data),
    enabled: !!ticketId,
  });
}

export function useSupportTicketAssignees() {
  return useQuery<SupportTicketAssigneesResponse, unknown, SupportTicketAssignee[]>({
    queryKey: supportTicketKeys.assignees(),
    queryFn: () => fetcher<SupportTicketAssigneesResponse>("/support-tickets/assignees"),
    select: (res) => res.data,
  });
}

export interface SystemSupportTicketListPage {
  items: SystemSupportTicketListItem[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

function buildSystemListParams(
  filter: SystemSupportTicketListFilter,
  search: string,
  page: number,
  limit: number,
  dir: "asc" | "desc" = "desc",
): Record<string, string> {
  const params: Record<string, string> = {
    page: String(page),
    limit: String(limit),
    // `sort` stays pinned to `createdAt`; only the direction is a caller's choice.
    //
    // Worth knowing what this overrides: the endpoint's own default is
    // `[{ priority: 'desc' }, { createdAt: 'asc' }]` — "urgent first, then oldest", the
    // order an ops person works a queue top-down — and its helper replaces that wholesale
    // rather than refining it when a `sort` is named. Naming one here has therefore always
    // traded triage order for date order; the toggle only exposes the direction that was
    // already fixed at `desc`. If triage order is wanted back, the change is to stop
    // sending `sort` at all, not to flip `dir`.
    sort: "createdAt",
    dir,
  };
  const trimmedSearch = search.trim();
  if (trimmedSearch) params.search = trimmedSearch;

  switch (filter) {
    case "UNASSIGNED":
      params.unassigned = "true";
      break;
    case "OPEN":
      params.openOnly = "true";
      break;
    case "RESOLVED":
      params.status = "RESOLVED";
      break;
    case "CLOSED":
      params.status = "CLOSED";
      break;
  }

  return params;
}

export function useSystemSupportTicketList(
  filter: SystemSupportTicketListFilter = "ALL",
  search = "",
  page = 1,
  limit = 20,
  dir: "asc" | "desc" = "desc",
) {
  return useQuery<SystemSupportTicketListResponse, unknown, SystemSupportTicketListPage>({
    // `dir` is part of the key: without it the two directions would share one cache entry
    // and flipping the toggle would serve the previous order from cache.
    queryKey: [
      ...supportTicketKeys.systemList(filter, search.trim(), page, limit),
      dir,
    ],
    queryFn: () =>
      fetcher<SystemSupportTicketListResponse>(
        `/support-tickets?${new URLSearchParams(buildSystemListParams(filter, search, page, limit, dir)).toString()}`,
      ),
    select: (res) => ({
      items: res.data.docs.map(withNormalizedId),
      pagination: res.data.pagination,
    }),
  });
}

export function useSupportTicketStats() {
  return useQuery<SupportTicketStatsResponse, unknown, SupportTicketStats>({
    queryKey: supportTicketKeys.stats(),
    queryFn: () => fetcher<SupportTicketStatsResponse>("/support-tickets/stats"),
    select: (res) => res.data,
  });
}

export function useSystemSupportTicketDetail(ticketId: string) {
  return useQuery<
    SystemSupportTicketDetailResponse,
    unknown,
    SystemSupportTicketDetail
  >({
    queryKey: supportTicketKeys.systemDetail(ticketId),
    queryFn: () =>
      fetcher<SystemSupportTicketDetailResponse>(`/support-tickets/${ticketId}`),
    select: (res) => withNormalizedId(res.data),
    enabled: !!ticketId,
  });
}
